// CMS result-code classification + submission-row predicates.
//
// Pure tier (no React, no I/O). Single source of truth for:
//   - the operator-facing label/explainer/tone per CMS result code, covering
//     BOTH code namespaces: $check-eligibility (ACCESSEligibilityResultCS) and
//     $align (ACCESSAlignmentResultCS);
//   - the terminal-detection + freshness predicates the CMS poll hook and both
//     modals share (eligibility = result_code present; align = terminal status
//     OR result_code present).
//
// Extracted from CmsEligibilityModal so the eligibility + alignment modals can't
// drift apart. Spec: docs/specs/2026-06-25-cms-tab-eligibility-fire-and-poll.md.

export type CmsTone = 'good' | 'moderate' | 'bad';
export interface CmsResultLabel {
  headline: string;
  explainer: string;
  tone: CmsTone;
}

// Minimal structural shape — avoids a runtime import from services/ (keeps this
// tier pure). The real row is CmsSubmissionRow in services/CmsService.
export interface CmsRowLike {
  correlation_id?: string | null;
  status?: string | null;
  result_code?: string | null;
}

// ── $check-eligibility — ACCESSEligibilityResultCS ──────────────────────────
const ELIGIBILITY_LABELS: Record<string, CmsResultLabel> = {
  eligible: {
    headline: 'Eligible',
    explainer: 'Patient is enrolled in Medicare Part B and meets ACCESS Model criteria. They can be aligned to the program.',
    tone: 'good',
  },
  'eligible-pending-diagnosis': {
    headline: 'Eligible · pending diagnosis',
    explainer: 'Medicare confirms enrollment but the qualifying diagnosis isn\'t yet on file with CMS. The MD can still accept; alignment will use the codes on the care plan.',
    tone: 'moderate',
  },
  'eligible-switch-participants': {
    headline: 'Eligible · switching from another participant',
    explainer: 'Patient is currently aligned with another ACCESS Model participant. Aligning will trigger a switch — the previous participant is notified and effective dates roll over per CMS rules.',
    tone: 'moderate',
  },
  'not-eligible-not-medicare': {
    headline: 'Not eligible · not on Medicare',
    explainer: 'CMS reports the patient is not enrolled in Medicare Part B (or coverage has lapsed). They are ineligible for any ACCESS Model program until coverage is active.',
    tone: 'bad',
  },
  'not-eligible-services': {
    headline: 'Not eligible · excluding services',
    explainer: 'Patient is receiving services that prevent ACCESS eligibility (e.g. ESRD on dialysis, hospice, end-of-life care). Clinical review required before any further outreach.',
    tone: 'bad',
  },
  'not-eligible-diagnoses': {
    headline: 'Not eligible · excluding diagnoses',
    explainer: 'CMS sees diagnoses on this patient\'s claims that disqualify them from the ACCESS Model (e.g. active malignancy, dementia, severe heart failure). Clinical review required.',
    tone: 'bad',
  },
  'not-eligible-control-group': {
    headline: 'Not eligible · CMS control group',
    explainer: 'Patient has been assigned to the CMS ACCESS Model control group. They cannot be aligned to any participant for the duration of the model. Do not retry.',
    tone: 'bad',
  },
  'not-eligible-already-aligned': {
    headline: 'Not eligible · already aligned',
    explainer: 'Patient is already aligned with another ACCESS Model participant and the agreement does not permit switching. Cannot enroll. Different from "eligible · switching" which allows a switch under CMS rules.',
    tone: 'bad',
  },
  'not-eligible-clinical-exclusion': {
    headline: 'Not eligible · clinical exclusion',
    explainer: 'CMS reports a clinical exclusion that prevents the patient from receiving services under the ACCESS Model. Clinical review required.',
    tone: 'bad',
  },
  'not-eligible-mismatch': {
    headline: 'Not eligible · identity mismatch',
    explainer: 'The patient information we sent (most often the MBI, name, or date of birth) doesn\'t match CMS records. Correct the patient record and retry.',
    tone: 'moderate',
  },
  'not-eligible-other': {
    headline: 'Not eligible',
    explainer: 'Patient is excluded for a reason CMS did not specify in the standard codes. See the detail line below for what CMS returned.',
    tone: 'bad',
  },
};

// ── $align — ACCESSAlignmentResultCS ────────────────────────────────────────
const ALIGN_LABELS: Record<string, CmsResultLabel> = {
  aligned: {
    headline: 'Aligned',
    explainer: 'CMS aligned the patient to the program. The care period is activated and post-alignment tasks are created.',
    tone: 'good',
  },
  'alignment-pending': {
    headline: 'Alignment pending',
    explainer: 'CMS accepted the alignment request but has not finalized it. The verdict will land on the CMS onboarding tab once CMS completes processing.',
    tone: 'moderate',
  },
  'not-aligned-not-medicare': {
    headline: 'Not aligned · not on Medicare',
    explainer: 'CMS reports the patient is not enrolled in Medicare Part B. Alignment cannot proceed until coverage is active.',
    tone: 'bad',
  },
  'not-aligned-services': {
    headline: 'Not aligned · excluding services',
    explainer: 'Patient is receiving services that prevent alignment (e.g. ESRD on dialysis, hospice). Clinical review required.',
    tone: 'bad',
  },
  'not-aligned-diagnoses': {
    headline: 'Not aligned · excluding diagnoses',
    explainer: 'CMS sees disqualifying diagnoses on the patient\'s claims. Clinical review required.',
    tone: 'bad',
  },
  'not-aligned-control-group': {
    headline: 'Not aligned · CMS control group',
    explainer: 'Patient is in the CMS ACCESS Model control group and cannot be aligned for the duration of the model. Do not retry.',
    tone: 'bad',
  },
  'not-aligned-already-aligned': {
    headline: 'Not aligned · already aligned',
    explainer: 'Patient is already aligned with another participant and the agreement does not permit switching.',
    tone: 'bad',
  },
  'not-aligned-clinical-exclusion': {
    headline: 'Not aligned · clinical exclusion',
    explainer: 'A clinical-exclusion condition prevents alignment. Clinical review required.',
    tone: 'bad',
  },
  'not-aligned-no-switch-attestation': {
    headline: 'Not aligned · missing switch attestation',
    explainer: 'A switch from another participant requires an attestation that was not provided. Resubmit with the attestation.',
    tone: 'moderate',
  },
  'not-aligned-mismatch': {
    headline: 'Not aligned · identity mismatch',
    explainer: 'CMS could not match the submitted identity (most often the MBI, name, or date of birth). Correct the patient record and resubmit.',
    tone: 'moderate',
  },
  'not-aligned': {
    headline: 'Not aligned',
    explainer: 'CMS declined the alignment for a reason it did not specify in the standard codes. See the detail line for what CMS returned.',
    tone: 'bad',
  },
};

// ── $unalign — ACCESSUnalignmentResultCS ────────────────────────────────────
const UNALIGN_LABELS: Record<string, CmsResultLabel> = {
  unaligned: {
    headline: 'Unaligned',
    explainer: 'CMS unaligned the patient from the ACCESS Model. The care period is closed and the patient is disenrolled.',
    tone: 'good',
  },
  'unalignment-pending': {
    headline: 'Unalignment pending review',
    explainer: 'CMS accepted the unalignment request but has not finalized it. The care period moves to pending-unalignment; the final verdict lands once CMS completes its review.',
    tone: 'moderate',
  },
  'patient-not-aligned': {
    headline: 'Patient not aligned',
    explainer: 'CMS reports this patient is not currently aligned to the program, so there is nothing to unalign. The care period is unchanged.',
    tone: 'moderate',
  },
  'cannot-unalign-during-lock-in': {
    headline: 'Cannot unalign · lock-in period',
    explainer: 'CMS will not unalign the patient during the mandatory lock-in period. The patient stays aligned and active — retry once the lock-in window has passed.',
    tone: 'moderate',
  },
};

// Op-agnostic codes a poll/result can carry from the client lifecycle, not CMS.
const LIFECYCLE_LABELS: Record<string, CmsResultLabel> = {
  'validation-failed': {
    headline: 'Input rejected',
    explainer: 'CMS rejected the request because we sent malformed or missing required information — most often the Medicare MBI or date of birth. Update the patient record and retry.',
    tone: 'moderate',
  },
  pending: {
    headline: 'CMS still processing',
    explainer: 'CMS accepted the request but did not return a final verdict within the allowed window. Our care team will review and follow up — no action needed right now.',
    tone: 'moderate',
  },
  unknown: {
    headline: 'Unknown response',
    explainer: 'CMS returned a response we don\'t recognize. The detail line below shows what we received — if this persists, the CMS spec may have changed.',
    tone: 'moderate',
  },
};

const LABELS: Record<string, CmsResultLabel> = {
  ...ELIGIBILITY_LABELS,
  ...ALIGN_LABELS,
  ...UNALIGN_LABELS,
  ...LIFECYCLE_LABELS,
};

const SUCCESS_CODES = new Set(['eligible', 'eligible-pending-diagnosis', 'eligible-switch-participants', 'aligned']);
// Codes the operator/patient can fix by correcting input then resubmitting.
const INVALID_MBI_CODES = new Set(['validation-failed', 'not-aligned-mismatch', 'not-eligible-mismatch']);

function norm(code: string | null | undefined): string {
  return (code == null ? '' : String(code)).trim().toLowerCase();
}

export const CmsResultCodes = {
  LABELS,
  /** Operator-facing label for a CMS result code, or null when unrecognized
   *  (caller falls back to the raw result_display). */
  labelFor(code: string | null | undefined): CmsResultLabel | null {
    return LABELS[norm(code)] ?? null;
  },
  isSuccess(code: string | null | undefined): boolean {
    return SUCCESS_CODES.has(norm(code));
  },
  isInvalidMbi(code: string | null | undefined): boolean {
    return INVALID_MBI_CODES.has(norm(code));
  },
  /** Terminal negative — a real CMS rejection (not a fixable input error). */
  isDeadEnd(code: string | null | undefined): boolean {
    const c = norm(code);
    if (!c || SUCCESS_CODES.has(c) || INVALID_MBI_CODES.has(c)) return false;
    return c.startsWith('not-eligible') || c.startsWith('not-aligned');
  },
  /** A submission row represents a positive outcome — success result_code, or a
   *  terminal-success lifecycle status with no contradicting code. */
  isAlignedRow(row: CmsRowLike | null | undefined): boolean {
    if (!row) return false;
    if (SUCCESS_CODES.has(norm(row.result_code))) return true;
    return !norm(row.result_code) && norm(row.status) === 'success';
  },
};

// ── Submission-row predicates (shared by the poll hook + both modals) ───────

/** A row carries a real CMS verdict when result_code is present and non-blank. */
export function hasResultCode(row: CmsRowLike | null | undefined): boolean {
  return !!row && norm(row.result_code) !== '';
}

// Align/unalign rows can reach a terminal lifecycle state without a result_code
// (e.g. a transport 'error'/'timeout', or 'rejected' before CMS replied). Unalign
// also writes 'unalignment-pending' as status='polling' WITH a result_code — CMS's
// final answer for the call — so a present result_code is terminal there too.
const ALIGN_TERMINAL_STATUS = new Set(['success', 'rejected', 'error', 'timeout']);

export type CmsOp = 'eligibility' | 'align' | 'unalign' | 'reporting';

/** Single terminal predicate consumed by the hook's refetchInterval AND each
 *  modal's verdict effect — so polling stops exactly when the modal resolves. */
export function isCmsSubmissionTerminal(op: CmsOp, row: CmsRowLike | null | undefined): boolean {
  if (!row) return false;
  if (op === 'eligibility') return hasResultCode(row);
  // align + unalign + reporting: terminal lifecycle status OR a present result_code.
  // report_data rows reach the same shape — WF37 writes status 'success'/'rejected'/'error'
  // alongside an ACCESSReportDataResultCS code, and 'error' is what a transport failure
  // records (never 'rejected', which would read as a CMS decision about the patient).
  return ALIGN_TERMINAL_STATUS.has(norm(row.status)) || hasResultCode(row);
}

/** Freshness via server-side identity (skew-immune): the row whose correlation_id
 *  was NOT present before we fired. Rows are newest-first; returns the first
 *  fresh row, or null. */
export function firstFreshRow<T extends CmsRowLike>(
  rows: readonly T[] | null | undefined,
  baselineIds: ReadonlySet<string>,
): T | null {
  if (!Array.isArray(rows)) return null;
  const fresh = rows.find((row) => {
    const id = row && row.correlation_id != null ? String(row.correlation_id) : '';
    return id !== '' && !baselineIds.has(id);
  });
  return fresh ?? null;
}

/** The fresh row IF it has reached a terminal verdict, else null. */
export function freshTerminalRow<T extends CmsRowLike>(
  rows: readonly T[] | null | undefined,
  baselineIds: ReadonlySet<string>,
  op: CmsOp,
): T | null {
  const fresh = firstFreshRow(rows, baselineIds);
  return fresh && isCmsSubmissionTerminal(op, fresh) ? fresh : null;
}
